// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  FieldSelect,
  FieldText,
  SectionCard,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import BusinessValidityChangeNotice from "components/BusinessValidityChangeNotice";
import { Icon } from "components/ShellV2";

// Utils
import { ALLOCATION_TYPES } from "utils/constants/index";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

const SETTLEMENT_SALE_PRICE_TYPES = [
  { value: "INDIVIDUAL", label: "INDIVIDUAL" },
  { value: "AGGREGATOR", label: "AGGREGATOR" },
];

function Settlement({ data, isLoading, onUpdate, readOnly }) {
  const initialStartDate = data?.start_date || lisbonToday();
  const initialEndDate = data?.end_date || defaultBusinessEndDate(initialStartDate);
  const [original, setOriginal] = useState({
    ...data,
    start_date: initialStartDate,
    end_date: initialEndDate,
  });

  const [methodAllocCoeff, setMethodAllocCoeff] = useState(data.method_alloc_coeff);
  const [lemTradingFee, setLemTradingFee] = useState(data.lem_trading_fee);
  const [settlementSalePriceType, setSettlementSalePriceType] = useState(
    data.settlement_sale_price_type || "INDIVIDUAL"
  );
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const canSave =
    !isLoading &&
    !(
      original.method_alloc_coeff === methodAllocCoeff &&
      original.lem_trading_fee === lemTradingFee &&
      (original.settlement_sale_price_type || "INDIVIDUAL") === settlementSalePriceType &&
      original.start_date === startDate &&
      original.end_date === endDate
    ) &&
    startDate &&
    (!endDate || endDate >= startDate);
  const { t } = useTranslation(["community", "common"]);

  const onUpdateClicked = async () => {
    if (canSave) {
      const newData = {
        method_alloc_coeff: methodAllocCoeff,
        lem_trading_fee: lemTradingFee,
        settlement_sale_price_type: settlementSalePriceType,
        start_date: startDate,
        end_date: endDate || null,
      };
      const success = await onUpdate(newData, t("settlement.updated_ok"));

      if (success) {
        setOriginal({ ...newData, end_date: endDate });
      }
    }
  };

  return (
    <SectionCard
      id="settlement"
      icon="receipt_long"
      title={t("settlement.title")}
      action={
        readOnly ? null : (
          <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
            <Icon name="save" />
            {t("settlement.update")}
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!readOnly && (
          <ValidityDateRangePicker
            className="sm:col-span-2"
            value={{ start: startDate, end: endDate }}
            onChange={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        )}
        <FieldSelect
          label={t("settlement.alloc_type")}
          value={methodAllocCoeff}
          options={ALLOCATION_TYPES}
          onChange={setMethodAllocCoeff}
          disabled={readOnly}
        />
        <FieldText
          label={t("settlement.lem_trading_fee")}
          type="number"
          value={lemTradingFee}
          onChange={(v) => setLemTradingFee(Number(v))}
          disabled={readOnly}
        />
        <FieldSelect
          label={t("settlement.settlement_sale_price_type")}
          value={settlementSalePriceType}
          options={SETTLEMENT_SALE_PRICE_TYPES}
          onChange={setSettlementSalePriceType}
          disabled={readOnly}
        />
      </div>
      {!readOnly && (
        <BusinessValidityChangeNotice
          originalStartDate={original.start_date}
          originalEndDate={original.end_date}
          startDate={startDate}
          endDate={endDate}
          className="mt-4"
        />
      )}
    </SectionCard>
  );
}

Settlement.defaultProps = {
  data: { method_alloc_coeff: "", lem_trading_fee: 0, settlement_sale_price_type: "INDIVIDUAL" },
  isLoading: false,
  onUpdate: () => {},
  readOnly: false,
};

Settlement.propTypes = {
  data: PropTypes.shape({
    method_alloc_coeff: PropTypes.string,
    lem_trading_fee: PropTypes.number,
    settlement_sale_price_type: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  onUpdate: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default Settlement;
